<template>
    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <div class="flex flex-wrap gap-6">
            <TextInput
                v-model="form.version"
                class="w-52 sm:w-80"
                :label="$t('vue.version')"
                type="text"
                placeholder="v. 1.5"
                :error="getError('version')"
            />

            <TextInput
                v-model="form.release_date"
                class="w-52"
                :label="$t('vue.release_date')"
                type="date"
                :error="getError('release_date')"
            />
        </div>

        <div class="space-y-6">
            <div
                v-for="(block, idx) in form.blocks"
                :key="idx"
                class="rounded-md bg-white p-6 shadow-card"
            >
                <div class="mb-4 flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-gray-700">
                        {{ $t("vue.changelog_block") }} #{{ idx + 1 }}
                    </h4>
                    <AtomicButton
                        size="sm"
                        color="red"
                        @click.prevent="removeBlock(idx)"
                    >
                        <template #icon>
                            <TrashIcon class="h-5 w-5" />
                        </template>
                    </AtomicButton>
                </div>

                <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div class="space-y-4">
                        <div
                            class="text-xs font-semibold uppercase text-gray-400"
                        >
                            {{ $t("vue.czech") }}
                        </div>
                        <TextInput
                            v-model="block.title_cs"
                            :label="$t('vue.title')"
                            type="text"
                            :placeholder="$t('vue.max_255_characters')"
                            :error="getError(`blocks.${idx}.title_cs`)"
                        />
                        <TextAreaInput
                            v-model="block.description_cs"
                            :label="$t('vue.description')"
                            :rows="3"
                            :error="getError(`blocks.${idx}.description_cs`)"
                        />
                        <TextAreaInput
                            v-model="block.items_cs"
                            :label="$t('vue.changelog_items')"
                            :secondarylabel="$t('vue.one_item_per_line')"
                            :rows="3"
                        />
                    </div>

                    <div class="space-y-4">
                        <div
                            class="text-xs font-semibold uppercase text-gray-400"
                        >
                            {{ $t("vue.english") }}
                        </div>
                        <TextInput
                            v-model="block.title_en"
                            :label="$t('vue.title')"
                            type="text"
                            :placeholder="$t('vue.max_255_characters')"
                            :error="getError(`blocks.${idx}.title_en`)"
                        />
                        <TextAreaInput
                            v-model="block.description_en"
                            :label="$t('vue.description')"
                            :rows="3"
                            :error="getError(`blocks.${idx}.description_en`)"
                        />
                        <TextAreaInput
                            v-model="block.items_en"
                            :label="$t('vue.changelog_items')"
                            :secondarylabel="$t('vue.one_item_per_line')"
                            :rows="3"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div>
            <AtomicButton size="sm" color="white" @click.prevent="addBlock">
                {{ $t("vue.add_changelog_block") }}
                <template #icon>
                    <PlusCircleIcon class="mr-2 h-6 w-6" />
                </template>
            </AtomicButton>
        </div>

        <div class="flex flex-row gap-2">
            <AtomicButton
                type="submit"
                color="yellow"
                size="sm"
                :disabled="processing || !isValid"
            >
                {{ submitLabel }}
            </AtomicButton>

            <slot name="actions" />
        </div>
    </form>
</template>

<script lang="ts" setup>
import { reactive, computed } from "vue";
import { PlusCircleIcon, TrashIcon } from "@heroicons/vue/24/outline";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type IChangelogEntry from "@/types/Changelog/IChangelogEntry";

interface BlockForm {
    title_cs: string;
    title_en: string;
    description_cs: string;
    description_en: string;
    items_cs: string;
    items_en: string;
}

interface EntryPayload {
    version: string;
    release_date: string;
    blocks: {
        title_cs: string;
        title_en: string;
        description_cs: string;
        description_en: string;
        items_cs: string[];
        items_en: string[];
    }[];
}

const props = defineProps<{
    entry?: IChangelogEntry;
    submitLabel: string;
    processing?: boolean;
    errors?: IInputErrors;
}>();

const emit = defineEmits<{
    (e: "submit", payload: EntryPayload): void;
}>();

const emptyBlock = (): BlockForm => ({
    title_cs: "",
    title_en: "",
    description_cs: "",
    description_en: "",
    items_cs: "",
    items_en: "",
});

const form = reactive<{
    version: string;
    release_date: string;
    blocks: BlockForm[];
}>({
    version: props.entry?.version ?? "",
    release_date: props.entry?.release_date
        ? props.entry.release_date.substring(0, 10)
        : "",
    blocks: props.entry?.blocks?.length
        ? props.entry.blocks.map((b) => ({
              title_cs: b.title_cs ?? "",
              title_en: b.title_en ?? "",
              description_cs: b.description_cs ?? "",
              description_en: b.description_en ?? "",
              items_cs: (b.items_cs ?? []).join("\n"),
              items_en: (b.items_en ?? []).join("\n"),
          }))
        : [emptyBlock()],
});

const isValid = computed(
    () =>
        form.version.trim() !== "" &&
        form.release_date !== "" &&
        form.blocks.length > 0 &&
        form.blocks.every(
            (b) => b.title_cs.trim() !== "" && b.title_en.trim() !== "",
        ),
);

const addBlock = () => {
    form.blocks.push(emptyBlock());
};

const removeBlock = (idx: number) => {
    form.blocks.splice(idx, 1);
};

const toItems = (value: string): string[] =>
    value
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "");

const onSubmit = () => {
    if (!isValid.value) {
        return;
    }

    emit("submit", {
        version: form.version,
        release_date: form.release_date,
        blocks: form.blocks.map((b) => ({
            title_cs: b.title_cs,
            title_en: b.title_en,
            description_cs: b.description_cs,
            description_en: b.description_en,
            items_cs: toItems(b.items_cs),
            items_en: toItems(b.items_en),
        })),
    });
};

const getError = (field: string): string => props.errors?.[field] ?? "";
</script>
